import React from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeftIcon, MapPinIcon, CalendarIcon, ClockIcon, DropletIcon, ThermometerIcon, UserIcon, AnchorIcon, CloudIcon, ImageIcon, EditIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Link } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
export const DiveDetail: React.FC = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  // Sample dive profile data
  const diveProfileData = [{
    time: 0,
    depth: 0
  }, {
    time: 5,
    depth: 10
  }, {
    time: 10,
    depth: 18
  }, {
    time: 15,
    depth: 22
  }, {
    time: 20,
    depth: 24
  }, {
    time: 25,
    depth: 24
  }, {
    time: 30,
    depth: 20
  }, {
    time: 35,
    depth: 15
  }, {
    time: 40,
    depth: 10
  }, {
    time: 45,
    depth: 5
  }, {
    time: 50,
    depth: 0
  }];
  // Sample dive details
  const dive = {
    id,
    location: 'Great Barrier Reef',
    site: 'Flynn Reef',
    date: 'June 15, 2023',
    time: '10:30 AM',
    depth: 24,
    avgDepth: 18,
    duration: 50,
    temperature: 24,
    visibility: 15,
    weather: 'Sunny',
    buddy: 'Sarah Johnson',
    diveType: 'Boat Dive',
    equipment: 'Full rental gear',
    notes: 'Saw a group of reef sharks and colorful coral formations. Water conditions were excellent with minimal current.',
    photos: ['https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80', 'https://images.unsplash.com/photo-1546026423-cc4642628d2b?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80', 'https://images.unsplash.com/photo-1534766438357-2b270dbd1b40?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80']
  };
  return <div data-id="element-929">
      <div className="mb-4" data-id="element-930">
        <Link to="/dives" className="flex items-center text-primary mb-4" data-id="element-931">
          <ArrowLeftIcon size={16} className="mr-1" data-id="element-932" />
          <span data-id="element-933">Back to Dive Log</span>
        </Link>
        <div className="flex justify-between items-start" data-id="element-934">
          <div data-id="element-935">
            <h1 className="text-2xl font-bold text-navy" data-id="element-936">{dive.location}</h1>
            <p className="text-gray-600 flex items-center" data-id="element-937">
              <MapPinIcon size={16} className="mr-1" data-id="element-938" />
              {dive.site}
            </p>
          </div>
          <Button variant="outline" icon={<EditIcon size={16} data-id="element-940" />} data-id="element-939">
            Edit
          </Button>
        </div>
      </div>
      <Card className="mb-6" data-id="element-941">
        <h2 className="font-semibold mb-4" data-id="element-942">Dive Profile</h2>
        <div className="h-64" data-id="element-943">
          <ResponsiveContainer width="100%" height="100%" data-id="element-944">
            <LineChart data={diveProfileData} margin={{
            top: 5,
            right: 5,
            left: 0,
            bottom: 5
          }} data-id="element-945">
              <CartesianGrid strokeDasharray="3 3" data-id="element-946" />
              <XAxis dataKey="time" label={{
              value: 'Time (min)',
              position: 'insideBottomRight',
              offset: -5
            }} data-id="element-947" />
              <YAxis label={{
              value: 'Depth (m)',
              angle: -90,
              position: 'insideLeft'
            }} reversed={true} data-id="element-948" />
              <Tooltip data-id="element-949" />
              <ReferenceLine y={18} stroke="#FF6B35" strokeDasharray="3 3" data-id="element-950" />
              <Line type="monotone" dataKey="depth" stroke="#0066CC" strokeWidth={2} dot={{
              r: 4
            }} activeDot={{
              r: 6
            }} data-id="element-951" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-id="element-952">
        <Card data-id="element-953">
          <p className="text-sm text-gray-500" data-id="element-954">Max Depth</p>
          <div className="flex items-center mt-1" data-id="element-955">
            <DropletIcon size={18} className="text-primary mr-2" data-id="element-956" />
            <span className="text-lg font-bold" data-id="element-957">{dive.depth}m</span>
          </div>
        </Card>
        <Card data-id="element-958">
          <p className="text-sm text-gray-500" data-id="element-959">Duration</p>
          <div className="flex items-center mt-1" data-id="element-960">
            <ClockIcon size={18} className="text-primary mr-2" data-id="element-961" />
            <span className="text-lg font-bold" data-id="element-962">{dive.duration} min</span>
          </div>
        </Card>
        <Card data-id="element-963">
          <p className="text-sm text-gray-500" data-id="element-964">Temperature</p>
          <div className="flex items-center mt-1" data-id="element-965">
            <ThermometerIcon size={18} className="text-primary mr-2" data-id="element-966" />
            <span className="text-lg font-bold" data-id="element-967">{dive.temperature}°C</span>
          </div>
        </Card>
        <Card data-id="element-968">
          <p className="text-sm text-gray-500" data-id="element-969">Visibility</p>
          <div className="flex items-center mt-1" data-id="element-970">
            <AnchorIcon size={18} className="text-primary mr-2" data-id="element-971" />
            <span className="text-lg font-bold" data-id="element-972">{dive.visibility}m</span>
          </div>
        </Card>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-973">
        <Card data-id="element-974">
          <h2 className="font-semibold mb-4" data-id="element-975">Dive Details</h2>
          <div className="grid grid-cols-2 gap-y-4" data-id="element-976">
            <div data-id="element-977">
              <p className="text-sm text-gray-500" data-id="element-978">Date</p>
              <p className="flex items-center" data-id="element-979">
                <CalendarIcon size={16} className="text-primary mr-1" data-id="element-980" />
                {dive.date}
              </p>
            </div>
            <div data-id="element-981">
              <p className="text-sm text-gray-500" data-id="element-982">Time</p>
              <p className="flex items-center" data-id="element-983">
                <ClockIcon size={16} className="text-primary mr-1" data-id="element-984" />
                {dive.time}
              </p>
            </div>
            <div data-id="element-985">
              <p className="text-sm text-gray-500" data-id="element-986">Weather</p>
              <p className="flex items-center" data-id="element-987">
                <CloudIcon size={16} className="text-primary mr-1" data-id="element-988" />
                {dive.weather}
              </p>
            </div>
            <div data-id="element-989">
              <p className="text-sm text-gray-500" data-id="element-990">Buddy</p>
              <p className="flex items-center" data-id="element-991">
                <UserIcon size={16} className="text-primary mr-1" data-id="element-992" />
                {dive.buddy}
              </p>
            </div>
          </div>
          <div className="mt-4" data-id="element-993">
            <p className="text-sm text-gray-500 mb-1" data-id="element-994">Dive Type</p>
            <div className="flex gap-2" data-id="element-995">
              <Badge variant="primary" data-id="element-996">{dive.diveType}</Badge>
              <Badge variant="accent" data-id="element-997">Recreational</Badge>
            </div>
          </div>
          <div className="mt-4" data-id="element-998">
            <p className="text-sm text-gray-500 mb-1" data-id="element-999">Equipment</p>
            <p data-id="element-1000">{dive.equipment}</p>
          </div>
          <div className="mt-4" data-id="element-1001">
            <p className="text-sm text-gray-500 mb-1" data-id="element-1002">Notes</p>
            <p data-id="element-1003">{dive.notes}</p>
          </div>
        </Card>
        <Card data-id="element-1004">
          <div className="flex justify-between items-center mb-4" data-id="element-1005">
            <h2 className="font-semibold" data-id="element-1006">Photos</h2>
            <Button variant="outline" size="sm" icon={<ImageIcon size={16} data-id="element-1008" />} data-id="element-1007">
              Add Photos
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-2" data-id="element-1009">
            {dive.photos.map((photo, index) => <div key={index} className="aspect-square rounded-lg overflow-hidden" data-id="element-1010">
                <img src={photo} alt={`Dive photo ${index + 1}`} className="w-full h-full object-cover" data-id="element-1011" />
              </div>)}
          </div>
        </Card>
      </div>
    </div>;
};